import { DragOverlay, Modifier } from '@dnd-kit/core';
import {
  DraggedItemOverlay,
  MultiSpaceOverlay,
  SingleGridSpaceOverlay
} from 'components/common/grid-space-overlay';
import { GridSpaceDataType, LevelType } from 'data/types';
import { useMemo } from 'react';
import { getActiveDraggedItem } from 'utils/drag-and-drop';

// Overlay dimensions for different item types
const OVERLAY_DIMENSIONS: Record<string, { width: number; height: number }> = {
  'new-space': { width: 100, height: 60 },
  'new-condenser': { width: 40, height: 40 },
  'new-ptac-unit': { width: 40, height: 40 },
  'new-condenser-ground': { width: 40, height: 40 },
  'new-condenser-wall': { width: 40, height: 40 },
  'new-condenser-roof': { width: 40, height: 40 },
  'new-air-handler': { width: 40, height: 40 },
  'new-air-handler-high-wall': { width: 40, height: 40 },
  'new-air-handler-low-wall': { width: 40, height: 40 },
  'new-air-handler-floor-ceiling': { width: 40, height: 40 },
  'new-air-handler-ceiling-cassette': { width: 40, height: 40 },
  'new-air-handler-ceiling-cassette-slim-fit': { width: 40, height: 40 },
  'new-air-handler-concealed-duct': { width: 40, height: 40 },
  'new-air-handler-ducted': { width: 40, height: 40 },
  'new-window-small': { width: 40, height: 40 },
  'new-window-medium': { width: 40, height: 40 },
  'new-window-large': { width: 40, height: 40 },
  'new-door-wood': { width: 40, height: 40 },
  'new-door-metal': { width: 40, height: 40 },
  'new-lineset': { width: 40, height: 40 }
};

type ConfigurationsDragOverlayProps = {
  activeId: string | null;
  activeDragData: { type?: string; space?: GridSpaceDataType };
  draggedSectionSpaces: GridSpaceDataType[];
  structure: LevelType[];
  showZones: boolean;
  easyMode: boolean;
  zoom?: number;
};

export default function ConfigurationsDragOverlay({
  activeId,
  activeDragData,
  draggedSectionSpaces,
  structure,
  showZones,
  easyMode,
  zoom = 1
}: ConfigurationsDragOverlayProps) {
  // Create a modifier that centers items from the list on the cursor
  const centerOnCursorModifier: Modifier = useMemo(
    () =>
      ({ transform, activeNodeRect, activatorEvent, active }) => {
        const itemType = active?.data?.current?.type as string | undefined;

        // Only apply centering for new items from the list
        if (!itemType || !itemType.startsWith('new-')) {
          return transform;
        }

        const dimensions = OVERLAY_DIMENSIONS[itemType];
        if (!dimensions || !activeNodeRect || !activatorEvent) {
          return transform;
        }

        // Calculate where the user clicked within the draggable element
        const event = activatorEvent as MouseEvent | TouchEvent;
        let clientX: number;
        let clientY: number;

        if ('touches' in event) {
          clientX = event.touches[0].clientX;
          clientY = event.touches[0].clientY;
        } else {
          clientX = event.clientX;
          clientY = event.clientY;
        }

        // Offset from top-left of the draggable element where user clicked
        const clickOffsetX = clientX - activeNodeRect.left;
        const clickOffsetY = clientY - activeNodeRect.top;

        // We want to center the overlay on cursor, so we need to:
        // 1. Add back the click offset (dnd-kit subtracts it)
        // 2. Subtract half the overlay dimensions
        const adjustX = clickOffsetX - dimensions.width / 2;
        const adjustY = clickOffsetY - dimensions.height / 2;

        return {
          ...transform,
          x: transform.x + adjustX,
          y: transform.y + adjustY
        };
      },
    []
  );

  const renderContent = () => {
    if (
      activeDragData?.type === 'grid-space' &&
      draggedSectionSpaces.length > 1
    ) {
      return (
        <MultiSpaceOverlay
          structure={structure}
          draggedSectionSpaces={draggedSectionSpaces}
          activeDragData={activeDragData}
          easyMode={easyMode}
          showZones={showZones}
          zoom={zoom}
        />
      );
    }

    if (activeDragData?.type === 'grid-space' && activeDragData?.space) {
      return (
        <SingleGridSpaceOverlay
          space={activeDragData.space}
          showZones={showZones}
          zoom={zoom}
        />
      );
    }

    const draggedItem = getActiveDraggedItem(activeId, activeDragData);

    if (!draggedItem) {
      return null;
    }

    return <DraggedItemOverlay draggedItem={draggedItem} zoom={zoom} />;
  };

  return (
    <DragOverlay modifiers={[centerOnCursorModifier]} dropAnimation={null}>
      {renderContent()}
    </DragOverlay>
  );
}
